import { useEffect, useRef, useState } from 'react'
import { Pipette } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cleanHex, hexToHsv, hsvToHex, parseColour } from '../../lib/inviteStyle'
import type { Hsv } from '../../lib/inviteStyle'

/**
 * The wildcard swatch: any colour at all, next to the ones a design suggests.
 *
 * A shade square over a hue rail for picking by eye, and a field that reads
 * whatever notation the host has their colour in — `#a1b2c3`, `rgb(...)`,
 * `hsl(...)`, `hsv(...)` or three bare numbers (lib/inviteStyle `parseColour`).
 *
 * The picker paints itself on every frame but only hands the colour outward
 * once the input settles — on pointer-up, or SETTLE ms after the last arrow
 * key or keystroke. The Design step's preview is a real iframe keyed on its
 * query string, so every value that escapes here reboots a copy of the app.
 */

type Props = {
  /** The colour the picker opens on; also what the trigger shows while active. */
  value: string
  onChange: (hex: string) => void
  /** True when this colour is the host's own pick rather than one of the swatches. */
  active?: boolean
  /** What this picker colours ("ink", "paper"), for the labels — both languages. */
  label: { en: string; ka: string }
  appLang?: string
  compact?: boolean
  testid?: string
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))

/** Long enough to swallow a held arrow key or a typed hex, short enough to feel live. */
const SETTLE = 220

const RAINBOW =
  'conic-gradient(from 0.25turn, #ff4d4d, #ffd24d, #4dff88, #4dd2ff, #8b5cf6, #ff4d8d, #ff4d4d)'

const HUE_RAIL =
  'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'

const ColourPicker = ({ value, onChange, active = false, label, appLang = 'en', compact = false, testid }: Props) => {
  const t = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const [open, setOpen] = useState(false)
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(cleanHex(value) || '#ff4d8d'))
  // null = show the current hex; a string = what the host is typing, empty included.
  const [typed, setTyped] = useState<string | null>(null)
  // Which pointer owns which rail, so two fingers on two rails cannot cross.
  const drag = useRef<{ id: number; hue: boolean } | null>(null)
  // The colour as of the last move, for handlers that read it outside a render
  // — a hex alone cannot carry the hue of a grey or a black.
  const latest = useRef(hsv)
  const settle = useRef<number | undefined>(undefined)
  const hex = hsvToHex(hsv)

  useEffect(() => () => window.clearTimeout(settle.current), [])

  const show = (next: Hsv) => {
    latest.current = next
    setHsv(next)
  }

  const commit = (next: Hsv, now: boolean) => {
    show(next)
    window.clearTimeout(settle.current)
    const out = hsvToHex(next)
    if (now) onChange(out)
    else settle.current = window.setTimeout(() => onChange(out), SETTLE)
  }

  // Follow the stored colour, unless the host is mid-drag or it is the very
  // colour this picker just committed. A value arriving from outside (a swatch
  // click) also cancels an edit still waiting to be handed over.
  useEffect(() => {
    const next = cleanHex(value)
    if (!next || drag.current || next === hsvToHex(latest.current)) return
    window.clearTimeout(settle.current)
    setTyped(null)
    show(hexToHsv(next))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const track = (e: React.PointerEvent<HTMLDivElement>, hue: boolean) => {
    const box = e.currentTarget.getBoundingClientRect()
    const x = clamp01((e.clientX - box.left) / box.width)
    const c = latest.current
    show(hue ? { ...c, h: x * 360 } : { ...c, s: x, v: clamp01(1 - (e.clientY - box.top) / box.height) })
  }

  const release = (e: React.PointerEvent<HTMLDivElement>, commitIt: boolean) => {
    if (drag.current?.id !== e.pointerId) return
    drag.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (commitIt) {
      setTyped(null)
      commit(latest.current, true)
    }
  }

  const dragProps = (hue: boolean) => ({
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (drag.current) return
      e.currentTarget.setPointerCapture(e.pointerId)
      drag.current = { id: e.pointerId, hue }
      track(e, hue)
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (drag.current?.id === e.pointerId) track(e, hue)
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => release(e, true),
    // A cancelled drag (Escape, a right-click, the popover closing under the
    // pointer) never sends pointerup; without these the rail would keep
    // following the cursor unpressed.
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => release(e, false),
    onLostPointerCapture: (e: React.PointerEvent<HTMLDivElement>) => release(e, false),
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      const arrow = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key)
      if (arrow < 0) return
      // The picker sits inside a dialog that pages through templates on ← / →.
      e.preventDefault()
      e.stopPropagation()
      const step = (e.shiftKey ? 5 : 1) * (arrow === 0 || arrow === 3 ? -1 : 1)
      const c = latest.current
      setTyped(null)
      commit(
        hue
          ? { ...c, h: (c.h + step * 2 + 360) % 360 }
          : arrow < 2
            ? { ...c, s: clamp01(c.s + step * 0.02) }
            : { ...c, v: clamp01(c.v + step * 0.02) },
        false,
      )
    },
  })

  const dot = compact ? 'h-7 w-7' : 'h-9 w-9'
  const thumb =
    'pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={t(`Any ${label.en} colour`, `ნებისმიერი ${label.ka} ფერი`)}
          aria-label={t(`Pick any ${label.en} colour`, `აირჩიე ნებისმიერი ${label.ka} ფერი`)}
          aria-pressed={active}
          className={`${dot} relative shrink-0 rounded-full border transition-all ${
            active
              ? 'border-transparent ring-2 ring-[#1b1033]/40 ring-offset-2 ring-offset-white dark:ring-white/80 dark:ring-offset-[#130b26]'
              : 'border-black/10 hover:scale-105 dark:border-white/20'
          }`}
          style={active ? { backgroundColor: cleanHex(value) || hex } : { backgroundImage: RAINBOW }}
          data-testid={testid}
        >
          {!active && (
            <span className="absolute inset-[3px] flex items-center justify-center rounded-full bg-white/90 dark:bg-[#130b26]/90">
              <Pipette className="h-3.5 w-3.5 text-[#4a3d66] dark:text-[#c3b8dd]" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[248px] space-y-3 p-3" align="start">
        <div
          role="slider"
          tabIndex={0}
          aria-label={t('Shade', 'ელფერი')}
          aria-valuetext={hex}
          className="relative h-32 w-full cursor-crosshair touch-none rounded-lg"
          style={{ backgroundColor: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }}
          data-testid={testid ? `${testid}-shade` : undefined}
          {...dragProps(false)}
        >
          <span className="absolute inset-0 rounded-lg" style={{ backgroundImage: 'linear-gradient(to right, #ffffff, rgba(255,255,255,0))' }} />
          <span className="absolute inset-0 rounded-lg" style={{ backgroundImage: 'linear-gradient(to top, #000000, rgba(0,0,0,0))' }} />
          <span className={thumb} style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: hex }} />
        </div>

        <div
          role="slider"
          tabIndex={0}
          aria-label={t('Hue', 'ტონი')}
          aria-valuenow={Math.round(hsv.h)}
          aria-valuemin={0}
          aria-valuemax={360}
          className="relative h-4 w-full cursor-pointer touch-none rounded-full"
          style={{ backgroundImage: HUE_RAIL }}
          data-testid={testid ? `${testid}-hue` : undefined}
          {...dragProps(true)}
        >
          <span
            className={thumb}
            style={{ left: `${(hsv.h / 360) * 100}%`, top: '50%', backgroundColor: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="h-8 w-8 shrink-0 rounded-lg border border-black/10 dark:border-white/20" style={{ backgroundColor: hex }} />
          <input
            value={typed === null ? hex : typed}
            onChange={(e) => {
              setTyped(e.target.value)
              const parsed = parseColour(e.target.value)
              if (parsed) commit(hexToHsv(parsed), false)
            }}
            onBlur={() => setTyped(null)}
            spellCheck={false}
            aria-label={t('Colour value', 'ფერის მნიშვნელობა')}
            placeholder="#7a6a3a"
            className="h-8 w-full min-w-0 rounded-lg border border-[#1b1033]/12 bg-white px-2 font-mono text-xs text-[#1b1033] outline-none focus:border-[#ff4d8d] dark:border-white/15 dark:bg-white/5 dark:text-[#f4f0ff]"
            data-testid={testid ? `${testid}-value` : undefined}
          />
        </div>
        <p className="text-[10px] leading-snug text-[#4a3d66] dark:text-[#c3b8dd]/80">
          {t('Paste a hex, rgb(), hsl() or hsv() value.', 'ჩასვი hex, rgb(), hsl() ან hsv() მნიშვნელობა.')}
        </p>
      </PopoverContent>
    </Popover>
  )
}

export default ColourPicker
